import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRight, ChevronLeft, X, Upload, Check } from 'lucide-react';
import theme from '../theme';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const TOTAL_STEPS = 6;

// ---------------- Option lists ----------------
const businessTypeOptions = [
  'E-commerce Brand', 'Brick-and-Mortar Retail', 'Service Business',
  'Creator / Influencer', 'Artist / Designer', 'Entertainment / Music',
  'Restaurant / Food', 'Beauty / Wellness', 'Automotive',
  'Education / Courses', 'Technology / SaaS', 'Events / Experiences',
  'Nonprofit / Community', 'Manufacturer', 'Other',
];

const industryOptions = [
  'Apparel', 'Beauty', 'Art', 'Music', 'Food & Beverage',
  'Automotive', 'Gaming', 'Sports', 'Education', 'Technology',
  'Health & Wellness', 'Entertainment', 'Events',
  'Professional Services', 'Consumer Goods', 'Other',
];

const offeringOptions = [
  'Physical Products', 'Digital Products', 'Services', 'Memberships',
  'Subscriptions', 'Events / Tickets', 'Experiences',
  'Courses / Education', 'Collectibles', 'Other',
];

const salesChannelOptions = [
  'My Website', 'Shopify', 'WooCommerce', 'In-Person / POS',
  'Social Media', 'Marketplace', 'Mobile App',
  'Invoice / Payment Link', 'Not Selling Yet', 'Other',
];

const transactionAmountOptions = [
  'Under $25', '$25–$50', '$51–$100', '$101–$250',
  '$251–$500', '$501–$1,000', '$1,000+', 'Varies Widely',
];

const purchaseFrequencyOptions = [
  'Multiple Times Per Month', 'Monthly', 'Every Few Months',
  'A Few Times Per Year', 'Usually One-Time Purchases', 'Not Sure Yet',
];

const existingProgramOptions = ['Yes', 'No', 'Currently Building One'];

const utilitiesAccessOptions = [
  'Discounts', 'Free Products', 'Recurring Benefits', 'VIP Access',
  'Early Product Drops', 'Exclusive Products', 'Events / Experiences',
  'Services', 'Upgrades', 'Maintenance', 'Membership Access',
  'Content', 'Community Access',
];

const ownershipOptions = [
  'Product Authenticity', 'Digital Proof of Ownership',
  'Transferable Benefits', 'Benefits Following Resale',
  'Collectible Ownership', 'Holder-Only Privileges',
];

const participationOptions = [
  'Voting / Governance', 'Customer Input on Future Products',
  'Community Decisions', 'Referral Incentives',
];

const advancedInterestOptions = [
  'Revenue-Sharing Structures', 'Holder Distributions', 'Contribution Pools',
  'Market-Linked Programs', 'Hold & Compound Strategies', 'Resale Participation',
  'Creator Royalties', 'Professionally Structured Kickbacks',
  'Kickback Architect Assistance', 'I Want to Learn More First',
];

const kickbackTypeOptions = [
  'Product', 'Product Collection', 'Service', 'Membership',
  'Subscription', 'Event / Experience', 'Customer Program', 'Other',
];

const supplyApproachOptions = [
  { value: 'fixed', label: 'Fixed Quantity' },
  { value: 'unlimited', label: 'Unlimited' },
  { value: 'not_sure', label: 'Not Sure Yet' },
];

const primaryGoalOptions = [
  'Increase Repeat Purchases',
  'Create Stronger Customer Relationships',
  'Add Continuing Value to Ownership',
  'Create Exclusivity',
  'Build Community',
  'Differentiate My Product',
  'Enable Resale / Transfer Benefits',
  'Prepare for Future Economic Participation',
  'Create a New Revenue Model',
  'Other',
];

// ---------------- Small render helpers ----------------
const inputStyle = {
  width: '100%', padding: '12px',
  border: `1px solid ${theme.colors.borders}`,
  borderRadius: theme.radius.sm, fontSize: '14px',
  color: theme.colors.text.primary, background: '#FFFFFF',
};

const labelStyle = {
  display: 'block', fontSize: '14px', fontWeight: 600,
  color: theme.colors.text.primary, marginBottom: '8px',
};

const groupHeadingStyle = {
  fontSize: '15px', fontWeight: 700, color: theme.colors.text.primary,
  marginBottom: '12px', marginTop: '8px',
};

const chipStyle = (selected) => ({
  padding: '10px 14px',
  border: `2px solid ${selected ? theme.colors.primary : theme.colors.borders}`,
  borderRadius: theme.radius.sm,
  background: selected ? `${theme.colors.primary}15` : '#FFFFFF',
  color: selected ? theme.colors.primary : theme.colors.text.secondary,
  fontSize: '13px', fontWeight: 500, cursor: 'pointer',
  transition: 'all 0.2s', textAlign: 'left',
});

const ChipGrid = ({ options, selected, onToggle, testIdPrefix, minColWidth = 150 }) => (
  <div style={{
    display: 'grid',
    gridTemplateColumns: `repeat(auto-fill, minmax(${minColWidth}px, 1fr))`,
    gap: '10px',
  }}>
    {options.map((opt) => {
      const value = typeof opt === 'string' ? opt : opt.value;
      const label = typeof opt === 'string' ? opt : opt.label;
      const isSelected = selected.includes(value);
      return (
        <button
          key={value}
          type="button"
          data-testid={testIdPrefix ? `${testIdPrefix}-${value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')}` : undefined}
          onClick={() => onToggle(value)}
          style={chipStyle(isSelected)}
        >
          {label}
        </button>
      );
    })}
  </div>
);

const SingleSelectGrid = ({ options, value, onChange, testIdPrefix, minColWidth = 180 }) => (
  <div style={{
    display: 'grid',
    gridTemplateColumns: `repeat(auto-fill, minmax(${minColWidth}px, 1fr))`,
    gap: '10px',
  }}>
    {options.map((opt) => {
      const optVal = typeof opt === 'string' ? opt : opt.value;
      const optLabel = typeof opt === 'string' ? opt : opt.label;
      const isSelected = value === optVal;
      return (
        <button
          key={optVal}
          type="button"
          data-testid={testIdPrefix ? `${testIdPrefix}-${optVal.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')}` : undefined}
          onClick={() => onChange(optVal)}
          style={chipStyle(isSelected)}
        >
          {optLabel}
        </button>
      );
    })}
  </div>
);

// =====================================================
// MAIN COMPONENT
// =====================================================
const CreatorOnboardingRebuilt = ({ user, setUser }) => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    // Step 1 — Business Identity
    profileImage: null,
    profileImagePreview: '',
    firstName: '',
    lastName: '',
    brandName: '',
    businessType: '',
    categories: [],
    otherCategory: '',
    website: '',
    socialProfile: '',
    country: '',
    state: '',
    city: '',

    // Step 2 — Business & Customers
    offeringTypes: [],
    salesChannels: [],
    transactionAmount: '',
    purchaseFrequency: '',
    existingProgram: '',
    existingProgramWhat: '',

    // Step 3 — Value Interests
    utilitiesAccess: [],
    ownership: [],
    participation: [],
    advancedInterests: [],

    // Step 4 — First Kickback direction (strategy only)
    kickbackType: '',
    kickbackName: '',
    kickbackDescription: '',
    kickbackPrice: '',
    supplyApproach: '',
    estimatedQuantity: '',
    primaryGoal: '',

    // Step 5 — Agreement (preserved)
    agreementAccepted: false,
    sendEmailCopy: false,

    // Retained preferences (kept out of onboarding UI, sent with sensible defaults
    // so backend persistence and dashboard behavior remain unchanged)
    emailNotifications: {
      portfolioReady: true,
      launchEvents: true,
      payoutCycles: true,
    },
    defaultDashboardView: 'overview',
  });

  const updateField = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const toggleArray = (field, value) => {
    setFormData((prev) => {
      const cur = prev[field] || [];
      return {
        ...prev,
        [field]: cur.includes(value) ? cur.filter((v) => v !== value) : [...cur, value],
      };
    });
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        updateField('profileImage', file);
        updateField('profileImagePreview', reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // ---------------- Continue gating ----------------
  const canProceed = () => {
    switch (currentStep) {
      case 1:
        return (
          formData.firstName.trim() &&
          formData.lastName.trim() &&
          formData.brandName.trim() &&
          formData.businessType &&
          formData.categories.length > 0 &&
          formData.country.trim()
        );
      case 2:
        return formData.offeringTypes.length > 0 && formData.salesChannels.length > 0;
      case 3:
        // Interests are optional; step is always advanceable
        return true;
      case 4:
        return !!formData.kickbackType && formData.kickbackName.trim() && !!formData.primaryGoal;
      case 5:
        return formData.agreementAccepted;
      case 6:
        return true;
      default:
        return false;
    }
  };

  const handleBack = () => {
    if (currentStep > 1) setCurrentStep((s) => s - 1);
  };

  const handleNext = () => {
    if (currentStep < TOTAL_STEPS) {
      setCurrentStep((s) => s + 1);
    }
  };

  const handleComplete = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${BACKEND_URL}/api/creator/onboard`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(formData),
      });
      if (!res.ok) {
        alert('Onboarding failed. Please try again.');
        return;
      }
      const userResponse = await fetch(`${BACKEND_URL}/api/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (userResponse.ok) {
        const updatedUser = await userResponse.json();
        setUser(updatedUser);
      }
    } catch (error) {
      console.error('Error completing onboarding:', error);
      alert('Error completing onboarding');
    } finally {
      setLoading(false);
    }
  };

  const goCreateFirstKickback = async () => {
    await handleComplete();
    // /creator/* is guarded by onboarding_complete; refresh triggers guard reset.
    window.location.href = '/creator/portfolios/new';
  };

  const goDashboard = async () => {
    await handleComplete();
    window.location.href = '/creator/dashboard';
  };

  // ---------------- Layout ----------------
  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #F8F9FB, #FFFFFF)',
      padding: '40px 20px',
      fontFamily: theme.fonts.ui,
    }}>
      <div style={{ maxWidth: '900px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '32px' }}>
          <div>
            <h1 style={{
              fontFamily: theme.fonts.heading,
              fontSize: '32px',
              fontWeight: 700,
              color: theme.colors.text.primary,
              marginBottom: '8px',
            }}>
              Creator Onboarding
            </h1>
            <p style={{ fontSize: '16px', color: theme.colors.text.secondary }} data-testid="onboarding-step-indicator">
              Step {currentStep} of {TOTAL_STEPS}
            </p>
          </div>
          <button
            data-testid="onboarding-exit-btn"
            onClick={() => navigate('/auth')}
            style={{
              padding: '8px 16px',
              background: 'transparent',
              border: `1px solid ${theme.colors.borders}`,
              borderRadius: theme.radius.sm,
              color: theme.colors.text.secondary,
              fontSize: '14px',
              cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: '4px',
            }}
          >
            <X size={16} /> Exit
          </button>
        </div>

        {/* Progress Bar */}
        <div style={{
          height: '4px',
          background: theme.colors.bg.tertiary,
          borderRadius: '2px',
          marginBottom: '40px',
          overflow: 'hidden',
        }}>
          <div style={{
            height: '100%',
            background: theme.colors.primary,
            width: `${(currentStep / TOTAL_STEPS) * 100}%`,
            transition: 'width 0.3s',
          }} />
        </div>

        <div style={{
          background: theme.colors.bg.card,
          borderRadius: theme.radius.card,
          padding: '40px',
          boxShadow: theme.colors.shadows.lg,
          border: `1px solid ${theme.colors.borders}`,
        }}>
          {/* ============ STEP 1: Business Identity ============ */}
          {currentStep === 1 && (
            <div data-testid="onboarding-step-1">
              <h2 style={{ fontSize: '24px', fontWeight: 700, color: theme.colors.text.primary, marginBottom: '8px' }}>
                Tell Us About Your Business
              </h2>
              <p style={{ fontSize: '14px', color: theme.colors.text.secondary, marginBottom: '24px' }}>
                Establish your Creator profile so we can personalize your workspace.
              </p>

              {/* Profile image */}
              <div style={{ marginBottom: '24px' }}>
                <label style={labelStyle}>Profile / Brand Image (optional)</label>
                {formData.profileImagePreview ? (
                  <div style={{ textAlign: 'center' }}>
                    <img
                      src={formData.profileImagePreview}
                      alt="Profile"
                      style={{ maxWidth: '160px', maxHeight: '160px', borderRadius: '50%', marginBottom: '12px', objectFit: 'cover' }}
                    />
                    <div>
                      <button
                        type="button"
                        data-testid="change-profile-image-btn"
                        onClick={() => { updateField('profileImagePreview', ''); updateField('profileImage', null); }}
                        style={{ padding: '8px 16px', background: theme.colors.bg.secondary, color: theme.colors.text.primary, border: `1px solid ${theme.colors.borders}`, borderRadius: theme.radius.sm, fontSize: '14px', cursor: 'pointer' }}
                      >
                        Change Image
                      </button>
                    </div>
                  </div>
                ) : (
                  <label style={{
                    display: 'inline-flex', alignItems: 'center', gap: '8px',
                    padding: '10px 16px',
                    border: `1px dashed ${theme.colors.borders}`,
                    borderRadius: theme.radius.sm, cursor: 'pointer',
                    fontSize: '14px', color: theme.colors.text.secondary,
                  }}>
                    <Upload size={16} /> Upload Profile Picture
                    <input type="file" accept="image/*" onChange={handleImageUpload} style={{ display: 'none' }} data-testid="profile-image-input" />
                  </label>
                )}
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
                <div>
                  <label style={labelStyle}>First Name *</label>
                  <input data-testid="first-name-input" type="text" placeholder="Enter first name" value={formData.firstName} onChange={(e) => updateField('firstName', e.target.value)} style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>Last Name *</label>
                  <input data-testid="last-name-input" type="text" placeholder="Enter last name" value={formData.lastName} onChange={(e) => updateField('lastName', e.target.value)} style={inputStyle} />
                </div>
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={labelStyle}>Business / Creator Name *</label>
                <input data-testid="brand-name-input" type="text" placeholder="Your business or creator name" value={formData.brandName} onChange={(e) => updateField('brandName', e.target.value)} style={inputStyle} />
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={labelStyle}>Business Type *</label>
                <SingleSelectGrid options={businessTypeOptions} value={formData.businessType} onChange={(v) => updateField('businessType', v)} testIdPrefix="business-type" />
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={labelStyle}>Primary Industry / Category * (Select all that apply)</label>
                <ChipGrid options={industryOptions} selected={formData.categories} onToggle={(v) => toggleArray('categories', v)} testIdPrefix="category" />
                {formData.categories.includes('Other') && (
                  <div style={{ marginTop: '12px' }}>
                    <input data-testid="other-category-input" type="text" placeholder="Specify other category" value={formData.otherCategory} onChange={(e) => updateField('otherCategory', e.target.value)} style={inputStyle} />
                  </div>
                )}
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
                <div>
                  <label style={labelStyle}>Website / Store URL</label>
                  <input data-testid="website-input" type="url" placeholder="https://yourbrand.com" value={formData.website} onChange={(e) => updateField('website', e.target.value)} style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>Primary Social Profile</label>
                  <input data-testid="social-profile-input" type="text" placeholder="@handle or profile URL (optional)" value={formData.socialProfile} onChange={(e) => updateField('socialProfile', e.target.value)} style={inputStyle} />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' }}>
                <div>
                  <label style={labelStyle}>Country *</label>
                  <input data-testid="country-input" type="text" placeholder="Country" value={formData.country} onChange={(e) => updateField('country', e.target.value)} style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>State / Province</label>
                  <input data-testid="state-input" type="text" placeholder="Optional" value={formData.state} onChange={(e) => updateField('state', e.target.value)} style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>City</label>
                  <input data-testid="city-input" type="text" placeholder="Optional" value={formData.city} onChange={(e) => updateField('city', e.target.value)} style={inputStyle} />
                </div>
              </div>
            </div>
          )}

          {/* ============ STEP 2: Business & Customers ============ */}
          {currentStep === 2 && (
            <div data-testid="onboarding-step-2">
              <h2 style={{ fontSize: '24px', fontWeight: 700, color: theme.colors.text.primary, marginBottom: '8px' }}>
                Tell Us What Customers Buy From You
              </h2>
              <p style={{ fontSize: '14px', color: theme.colors.text.secondary, marginBottom: '24px' }}>
                Help Kickback Contract understand how your business currently sells so we can better guide your Kickback setup.
              </p>

              <div style={{ marginBottom: '24px' }}>
                <label style={labelStyle}>What do you primarily sell? * (Select all that apply)</label>
                <ChipGrid options={offeringOptions} selected={formData.offeringTypes} onToggle={(v) => toggleArray('offeringTypes', v)} testIdPrefix="offering" />
              </div>

              <div style={{ marginBottom: '24px' }}>
                <label style={labelStyle}>Where do customers currently buy from you? * (Select all that apply)</label>
                <ChipGrid options={salesChannelOptions} selected={formData.salesChannels} onToggle={(v) => toggleArray('salesChannels', v)} testIdPrefix="channel" />
              </div>

              <div style={{ marginBottom: '24px' }}>
                <label style={labelStyle}>Typical Customer Transaction Amount</label>
                <SingleSelectGrid options={transactionAmountOptions} value={formData.transactionAmount} onChange={(v) => updateField('transactionAmount', v)} testIdPrefix="tx-amount" minColWidth={140} />
              </div>

              <div style={{ marginBottom: '24px' }}>
                <label style={labelStyle}>How frequently do customers typically buy from you?</label>
                <SingleSelectGrid options={purchaseFrequencyOptions} value={formData.purchaseFrequency} onChange={(v) => updateField('purchaseFrequency', v)} testIdPrefix="frequency" minColWidth={200} />
              </div>

              <div>
                <label style={labelStyle}>Do you already have a loyalty, rewards, membership, NFT, or customer-benefit program?</label>
                <SingleSelectGrid options={existingProgramOptions} value={formData.existingProgram} onChange={(v) => updateField('existingProgram', v)} testIdPrefix="existing-program" minColWidth={200} />
                {formData.existingProgram === 'Yes' && (
                  <div style={{ marginTop: '12px' }}>
                    <label style={{ ...labelStyle, fontWeight: 500 }}>What are you currently using?</label>
                    <input data-testid="existing-program-what-input" type="text" placeholder="e.g. Smile.io, custom NFT drop, Klaviyo tier program" value={formData.existingProgramWhat} onChange={(e) => updateField('existingProgramWhat', e.target.value)} style={inputStyle} />
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ============ STEP 3: Value Interests ============ */}
          {currentStep === 3 && (
            <div data-testid="onboarding-step-3">
              <h2 style={{ fontSize: '24px', fontWeight: 700, color: theme.colors.text.primary, marginBottom: '8px' }}>
                What Should Happen After Your Customer Buys?
              </h2>
              <p style={{ fontSize: '14px', color: theme.colors.text.secondary, marginBottom: '24px' }}>
                Kickback Contract turns the transaction into the beginning of an ongoing value relationship. Select the kinds of value you may want your customers or holders to receive.
              </p>

              <div style={{ marginBottom: '24px' }}>
                <h3 style={groupHeadingStyle}>Utilities & Access</h3>
                <ChipGrid options={utilitiesAccessOptions} selected={formData.utilitiesAccess} onToggle={(v) => toggleArray('utilitiesAccess', v)} testIdPrefix="utility" />
              </div>

              <div style={{ marginBottom: '24px' }}>
                <h3 style={groupHeadingStyle}>Ownership</h3>
                <ChipGrid options={ownershipOptions} selected={formData.ownership} onToggle={(v) => toggleArray('ownership', v)} testIdPrefix="ownership" />
              </div>

              <div style={{ marginBottom: '24px' }}>
                <h3 style={groupHeadingStyle}>Participation</h3>
                <ChipGrid options={participationOptions} selected={formData.participation} onToggle={(v) => toggleArray('participation', v)} testIdPrefix="participation" />
              </div>

              <div>
                <h3 style={groupHeadingStyle}>Advanced / Future Economic Participation</h3>
                <div style={{
                  background: theme.colors.bg.secondary,
                  border: `1px solid ${theme.colors.borders}`,
                  borderRadius: theme.radius.sm,
                  padding: '14px 16px',
                  marginBottom: '12px',
                  fontSize: '13px',
                  color: theme.colors.text.secondary,
                  lineHeight: '1.6',
                }}>
                  Kickback Contract is being developed to support advanced programs that may allow eligible holders to participate economically in the ecosystems surrounding what they own. These capabilities may require additional eligibility, providers, agreements, disclosures, and platform activation before use.
                </div>
                <ChipGrid options={advancedInterestOptions} selected={formData.advancedInterests} onToggle={(v) => toggleArray('advancedInterests', v)} testIdPrefix="advanced-interest" />
              </div>
            </div>
          )}

          {/* ============ STEP 4: First Kickback Direction ============ */}
          {currentStep === 4 && (
            <div data-testid="onboarding-step-4">
              <h2 style={{ fontSize: '24px', fontWeight: 700, color: theme.colors.text.primary, marginBottom: '8px' }}>
                What Would You Like to Turn Into Your First Kickback?
              </h2>
              <p style={{ fontSize: '14px', color: theme.colors.text.secondary, marginBottom: '24px' }}>
                You do not need to build it yet. This information helps Kickback Contract prepare a better starting point for your workspace.
              </p>

              <div style={{ marginBottom: '20px' }}>
                <label style={labelStyle}>Kickback Type *</label>
                <SingleSelectGrid options={kickbackTypeOptions} value={formData.kickbackType} onChange={(v) => updateField('kickbackType', v)} testIdPrefix="kickback-type" />
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={labelStyle}>Product / Program Name *</label>
                <input data-testid="kickback-name-input" type="text" placeholder="Give it a working name" value={formData.kickbackName} onChange={(e) => updateField('kickbackName', e.target.value)} style={inputStyle} />
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={labelStyle}>Short Description</label>
                <textarea
                  data-testid="kickback-description-input"
                  placeholder="Optional — one or two sentences"
                  value={formData.kickbackDescription}
                  onChange={(e) => updateField('kickbackDescription', e.target.value)}
                  rows={3}
                  style={{ ...inputStyle, resize: 'vertical', fontFamily: theme.fonts.ui }}
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
                <div>
                  <label style={labelStyle}>Approximate Price</label>
                  <input data-testid="kickback-price-input" type="text" placeholder="Optional — e.g. $79" value={formData.kickbackPrice} onChange={(e) => updateField('kickbackPrice', e.target.value)} style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>Participation / Supply Approach</label>
                  <SingleSelectGrid options={supplyApproachOptions} value={formData.supplyApproach} onChange={(v) => updateField('supplyApproach', v)} testIdPrefix="supply-approach" minColWidth={130} />
                </div>
              </div>

              {formData.supplyApproach === 'fixed' && (
                <div style={{ marginBottom: '20px' }}>
                  <label style={labelStyle}>Estimated Quantity</label>
                  <input
                    data-testid="estimated-quantity-input"
                    type="text"
                    inputMode="numeric"
                    placeholder="Approximate number for planning"
                    value={formData.estimatedQuantity}
                    onChange={(e) => updateField('estimatedQuantity', e.target.value)}
                    style={inputStyle}
                  />
                  <p style={{ fontSize: '12px', color: theme.colors.text.secondary, marginTop: '6px', lineHeight: '1.5' }}>
                    This estimate is for onboarding strategy only. It does not set contract supply, portfolio quantity, or smart-contract max supply.
                  </p>
                </div>
              )}

              <div>
                <label style={labelStyle}>Primary Goal *</label>
                <SingleSelectGrid options={primaryGoalOptions} value={formData.primaryGoal} onChange={(v) => updateField('primaryGoal', v)} testIdPrefix="primary-goal" minColWidth={220} />
              </div>
            </div>
          )}

          {/* ============ STEP 5: Platform Agreement ============ */}
          {currentStep === 5 && (
            <div data-testid="onboarding-step-5">
              <h2 style={{ fontSize: '24px', fontWeight: 700, color: theme.colors.text.primary, marginBottom: '24px' }}>
                Platform Agreement & Program Terms
              </h2>

              <div style={{
                background: theme.colors.bg.secondary,
                padding: '24px',
                borderRadius: theme.radius.md,
                marginBottom: '24px',
                border: `1px solid ${theme.colors.borders}`,
                maxHeight: '400px',
                overflowY: 'auto',
              }}>
                <h3 style={{
                  fontSize: '18px',
                  fontWeight: 700,
                  color: theme.colors.text.primary,
                  marginBottom: '4px',
                  textAlign: 'center',
                }}>
                  KICKBACK CONTRACT
                </h3>
                <h4 style={{
                  fontSize: '15px',
                  fontWeight: 600,
                  color: theme.colors.text.primary,
                  marginBottom: '4px',
                  textAlign: 'center',
                }}>
                  CREATOR PLATFORM AGREEMENT & PROGRAM TERMS
                </h4>
                <p style={{
                  fontSize: '13px',
                  color: theme.colors.text.secondary,
                  marginBottom: '4px',
                  textAlign: 'center',
                  fontStyle: 'italic',
                }}>
                  Pilot / Early Access Version
                </p>
                <p style={{
                  fontSize: '13px',
                  color: theme.colors.text.secondary,
                  marginBottom: '20px',
                  textAlign: 'center',
                }}>
                  Operator: BYTES LOGIC CORPORATION
                </p>
                <div style={{ fontSize: '14px', lineHeight: '1.8', color: theme.colors.text.secondary }}>
                  <p style={{ marginBottom: '8px' }}><strong>1. PURPOSE OF THIS AGREEMENT</strong></p>
                  <p style={{ marginBottom: '10px' }}>
                    This Creator Platform Agreement governs a Creator's access to and use of Kickback Contract, including participation in pilot, early-access, subscription, utility, ownership, redemption, marketplace, and future advanced economic-participation features.
                  </p>
                  <p style={{ marginBottom: '10px' }}>
                    Kickback Contract is designed to help businesses, entrepreneurs, brands, service providers, creators, and organizations transform ordinary transactions into continuing ownership and value relationships.
                  </p>
                  <p style={{ marginBottom: '10px' }}>
                    Depending upon the features available to the Creator's account, a Kickback may connect a product, service, membership, event, experience, or other eligible offering with digital ownership records, customer utilities, recurring benefits, access rights, verification, redemption opportunities, transferability rules, community features, and other Creator-configured experiences.
                  </p>
                  <p style={{ marginBottom: '16px' }}>
                    Certain advanced functionality described in this Agreement is still under development, may not be available during the Creator's current subscription or pilot, and may require additional eligibility, agreements, third-party providers, compliance review, or platform activation before use.
                  </p>

                  <p style={{ marginBottom: '8px' }}><strong>2. CURRENT KICKBACK FUNCTIONALITY</strong></p>
                  <p style={{ marginBottom: '10px' }}>
                    During the current stage of the platform, Kickback Contract may allow eligible Creators to create and configure Kickbacks connected to qualifying products, services, memberships, experiences, or other offerings.
                  </p>
                  <p style={{ marginBottom: '10px' }}>
                    Depending upon the Creator's plan and available platform features, a Creator may be able to configure:
                  </p>
                  <ul style={{ marginBottom: '10px', paddingLeft: '20px' }}>
                    <li>ownership-linked utilities</li>
                    <li>recurring or one-time customer benefits</li>
                    <li>discounts and promotional benefits</li>
                    <li>exclusive access</li>
                    <li>product or ownership verification</li>
                    <li>redemption rules</li>
                    <li>holder eligibility</li>
                    <li>utility timing and availability</li>
                    <li>transferable or non-transferable benefits</li>
                    <li>quantity or supply limitations</li>
                    <li>digital ownership records</li>
                    <li>artwork and associated media</li>
                    <li>customer or holder experiences</li>
                    <li>launch-readiness settings</li>
                    <li>marketplace-related functionality where available</li>
                    <li>additional features released by Kickback Contract</li>
                  </ul>
                  <p style={{ marginBottom: '16px' }}>
                    Availability may differ by subscription level, account type, pilot status, jurisdiction, implementation method, and technical readiness.
                  </p>

                  <p style={{ marginBottom: '8px' }}><strong>3. CREATOR-CONTROLLED PROGRAMS</strong></p>
                  <p style={{ marginBottom: '10px' }}>
                    Creators control the business terms of the Kickbacks they create, subject to platform limitations and applicable requirements.
                  </p>
                  <p style={{ marginBottom: '10px' }}>The Creator is responsible for determining:</p>
                  <ul style={{ marginBottom: '10px', paddingLeft: '20px' }}>
                    <li>what product, service, membership, or experience is connected to the Kickback</li>
                    <li>what utilities or benefits will be offered</li>
                    <li>who is eligible to receive those benefits</li>
                    <li>when benefits may be redeemed</li>
                    <li>whether benefits are recurring, limited, conditional, or one-time</li>
                    <li>whether eligible benefits transfer with ownership</li>
                    <li>what quantity or supply applies</li>
                    <li>how promised products, services, utilities, benefits, or experiences will be fulfilled</li>
                  </ul>
                  <p style={{ marginBottom: '16px' }}>
                    Kickback Contract provides infrastructure for configuring and administering these programs but does not assume responsibility for a Creator's underlying goods, services, fulfillment obligations, inventory, warranties, representations, customer service obligations, or independent business commitments unless specifically stated otherwise.
                  </p>

                  <p style={{ marginBottom: '8px' }}><strong>4. CREATOR RESPONSIBILITIES</strong></p>
                  <p style={{ marginBottom: '10px' }}>
                    The Creator is responsible for the accuracy, legality, and fulfillment of information and commitments submitted through the platform.
                  </p>
                  <p style={{ marginBottom: '10px' }}>This includes responsibility for:</p>
                  <ul style={{ marginBottom: '10px', paddingLeft: '20px' }}>
                    <li>product and service descriptions</li>
                    <li>pricing</li>
                    <li>images and media</li>
                    <li>intellectual-property rights</li>
                    <li>utility descriptions</li>
                    <li>benefit availability</li>
                    <li>eligibility rules</li>
                    <li>redemption requirements</li>
                    <li>inventory</li>
                    <li>customer communications</li>
                    <li>taxes</li>
                    <li>refunds relating to the Creator's underlying business</li>
                    <li>consumer-protection obligations</li>
                    <li>fulfillment of Creator-promised utilities or benefits</li>
                    <li>compliance with laws and requirements applicable to the Creator's business</li>
                  </ul>
                  <p style={{ marginBottom: '16px' }}>
                    A Creator may not knowingly publish fraudulent, deceptive, misleading, unlawful, infringing, or unavailable offers through Kickback Contract.
                  </p>

                  <p style={{ marginBottom: '8px' }}><strong>5. HOLDER VALUE AND CONTINUING BENEFITS</strong></p>
                  <p style={{ marginBottom: '10px' }}>
                    Kickback Contract is designed around the principle that a transaction can continue creating value after checkout.
                  </p>
                  <p style={{ marginBottom: '10px' }}>
                    A Kickback may allow a holder to receive or access benefits connected to qualifying ownership or participation.
                  </p>
                  <p style={{ marginBottom: '10px' }}>
                    Such benefits may include discounts, products, services, exclusive experiences, early access, recurring utilities, special privileges, verified ownership, transferable benefits where enabled, or other Creator-defined value.
                  </p>
                  <p style={{ marginBottom: '10px' }}>
                    The existence of a Kickback does not guarantee that every utility or benefit has monetary value, resale value, appreciation, income potential, or transferability.
                  </p>
                  <p style={{ marginBottom: '16px' }}>
                    The specific rights of the holder are determined by the rules attached to the applicable Kickback.
                  </p>

                  <p style={{ marginBottom: '8px' }}><strong>6. DIGITAL OWNERSHIP, BLOCKCHAIN AND TRANSFERABILITY</strong></p>
                  <p style={{ marginBottom: '10px' }}>
                    Kickback Contract may use blockchain or other digital-record technologies to support ownership records, digital assets, verification, transferability, smart-contract functionality, or related platform services.
                  </p>
                  <p style={{ marginBottom: '10px' }}>
                    Where blockchain-based functionality is enabled, transactions or records written to a blockchain may not be reversible through ordinary platform controls.
                  </p>
                  <p style={{ marginBottom: '10px' }}>
                    A Kickback or associated digital asset should not automatically be interpreted as an investment, security, financial product, guaranteed asset, or promise of profit merely because blockchain or digital ownership technology is involved.
                  </p>
                  <p style={{ marginBottom: '16px' }}>
                    The legal treatment of a particular program depends upon its actual structure, terms, economic characteristics, marketing, and use.
                  </p>

                  <p style={{ marginBottom: '8px' }}><strong>7. FUTURE ECONOMIC-PARTICIPATION CAPABILITIES</strong></p>
                  <p style={{ marginBottom: '10px' }}>
                    Kickback Contract is being developed to support more advanced models in which commerce may potentially create forms of continuing economic participation for eligible holders.
                  </p>
                  <p style={{ marginBottom: '10px' }}>Future capabilities may include structures involving:</p>
                  <ul style={{ marginBottom: '10px', paddingLeft: '20px' }}>
                    <li>contribution mechanisms</li>
                    <li>dedicated or pooled program funds</li>
                    <li>third-party custody</li>
                    <li>professional financial management</li>
                    <li>revenue-sharing structures</li>
                    <li>distributions</li>
                    <li>performance-based benefits</li>
                    <li>market-linked strategies</li>
                    <li>holder participation programs</li>
                    <li>Creator-defined economic benefits</li>
                    <li>regulated financial providers</li>
                    <li>broker or Kickback Architect participation</li>
                    <li>voting or governance features</li>
                    <li>other economic-participation models</li>
                  </ul>
                  <p style={{ marginBottom: '10px' }}>
                    These features are NOT automatically included with a Kickback and should not be interpreted as currently available merely because they are described in this Agreement.
                  </p>
                  <p style={{ marginBottom: '10px' }}>
                    Any such feature will only become available where specifically enabled by Kickback Contract and may require additional contracts, disclosures, eligibility requirements, identity verification, jurisdictional restrictions, licensed or otherwise qualified third-party providers, compliance procedures, and other conditions.
                  </p>
                  <p style={{ marginBottom: '16px' }}>
                    Kickback Contract may determine that a proposed structure cannot be activated through the platform.
                  </p>

                  <p style={{ marginBottom: '8px' }}><strong>8. NO AUTOMATIC PROMISE OF INCOME OR INVESTMENT RETURN</strong></p>
                  <p style={{ marginBottom: '10px' }}>
                    Kickback Contract does not guarantee that purchasing, receiving, holding, transferring, or participating in a Kickback will produce income, appreciation, investment profits, resale profits, distributions, or financial returns.
                  </p>
                  <p style={{ marginBottom: '16px' }}>
                    Where future economic-participation functionality is specifically offered, the applicable terms, disclosures, risks, eligibility requirements, and provider arrangements will be presented separately before participation.
                  </p>

                  <p style={{ marginBottom: '8px' }}><strong>9. KICKBACK ARCHITECTS AND THIRD-PARTY PROFESSIONALS</strong></p>
                  <p style={{ marginBottom: '10px' }}>
                    Kickback Contract may provide access to individuals or organizations that assist Creators with strategy, program design, configuration, implementation, analytics, financial structures, or other services.
                  </p>
                  <p style={{ marginBottom: '10px' }}>
                    Certain professionals may be referred to within the ecosystem as Kickback Architects.
                  </p>
                  <p style={{ marginBottom: '10px' }}>
                    The responsibilities, qualifications, permissions, compensation, and regulatory status of a Kickback Architect may vary according to the service being provided.
                  </p>
                  <p style={{ marginBottom: '10px' }}>
                    No person should be represented as a licensed broker, investment adviser, custodian, financial professional, or other regulated provider solely because that person participates in the Kickback Contract ecosystem.
                  </p>
                  <p style={{ marginBottom: '16px' }}>
                    Where a regulated professional or licensed provider is required, access to the applicable functionality may be conditioned upon participation by an appropriately qualified provider.
                  </p>

                  <p style={{ marginBottom: '8px' }}><strong>10. THIRD-PARTY PROVIDERS</strong></p>
                  <p style={{ marginBottom: '10px' }}>
                    Certain Kickback Contract functionality may depend upon outside companies or service providers, including payment processors, blockchain networks, ecommerce systems, communication providers, identity-verification services, marketplaces, custodians, financial institutions, or other technology providers.
                  </p>
                  <p style={{ marginBottom: '10px' }}>
                    Kickback Contract cannot guarantee uninterrupted operation of independent third-party systems.
                  </p>
                  <p style={{ marginBottom: '16px' }}>
                    Features dependent upon such systems may be modified, delayed, suspended, restricted, or discontinued if the relevant service becomes unavailable or materially changes.
                  </p>

                  <p style={{ marginBottom: '8px' }}><strong>11. PILOT AND EARLY-ACCESS FEATURES</strong></p>
                  <p style={{ marginBottom: '10px' }}>
                    Creators participating in the Founding Creator Pilot or another early-access program acknowledge that portions of Kickback Contract remain under active development.
                  </p>
                  <p style={{ marginBottom: '10px' }}>Pilot functionality may be:</p>
                  <ul style={{ marginBottom: '10px', paddingLeft: '20px' }}>
                    <li>incomplete</li>
                    <li>manually assisted</li>
                    <li>modified during testing</li>
                    <li>temporarily unavailable</li>
                    <li>subject to configuration changes</li>
                    <li>replaced by updated workflows</li>
                    <li>limited to selected participants</li>
                  </ul>
                  <p style={{ marginBottom: '10px' }}>
                    Creators may be asked to report bugs, workflow friction, usability issues, implementation results, or other product feedback.
                  </p>
                  <p style={{ marginBottom: '16px' }}>
                    Participation in a pilot does not guarantee that every planned feature will become commercially available or become available by a specific date.
                  </p>

                  <p style={{ marginBottom: '8px' }}><strong>12. CREATOR COMMITMENTS TO HOLDERS</strong></p>
                  <p style={{ marginBottom: '10px' }}>
                    A Creator should only publish a utility, benefit, reward, entitlement, or other commitment that the Creator reasonably expects to fulfill.
                  </p>
                  <p style={{ marginBottom: '10px' }}>
                    Once a holder qualifies for a Creator-promised benefit according to the applicable Kickback rules, the Creator remains responsible for honoring that obligation unless the published terms specifically provide otherwise.
                  </p>
                  <p style={{ marginBottom: '16px' }}>
                    Kickback Contract may suspend, restrict, flag, or remove a Kickback where there is evidence of fraud, misleading representations, repeated fulfillment failures, prohibited conduct, or material risk to holders or the platform.
                  </p>

                  <p style={{ marginBottom: '8px' }}><strong>13. REDEMPTION AND ELIGIBILITY</strong></p>
                  <p style={{ marginBottom: '10px' }}>
                    Utilities may include eligibility conditions, redemption periods, quantities, expiration rules, transfer rules, usage limitations, or other Creator-defined requirements.
                  </p>
                  <p style={{ marginBottom: '10px' }}>
                    The holder is responsible for satisfying applicable eligibility requirements.
                  </p>
                  <p style={{ marginBottom: '10px' }}>
                    The Creator is responsible for honoring valid redemptions according to the published rules.
                  </p>
                  <p style={{ marginBottom: '16px' }}>
                    Kickback Contract may provide technology used to record, verify, approve, reject, or track redemption activity.
                  </p>

                  <p style={{ marginBottom: '8px' }}><strong>14. TRANSFERS AND SECONDARY ACTIVITY</strong></p>
                  <p style={{ marginBottom: '10px' }}>
                    Where transferability is enabled, a Kickback or associated ownership record may be transferred according to the applicable smart contract, platform rules, marketplace capabilities, or Creator settings.
                  </p>
                  <p style={{ marginBottom: '10px' }}>Not every benefit must transfer with ownership.</p>
                  <p style={{ marginBottom: '10px' }}>Creators must clearly identify whether benefits are:</p>
                  <ul style={{ marginBottom: '10px', paddingLeft: '20px' }}>
                    <li>asset-bound</li>
                    <li>holder-bound</li>
                    <li>non-transferable</li>
                    <li>subject to additional eligibility after transfer</li>
                  </ul>
                  <p style={{ marginBottom: '10px' }}>Secondary-market availability is not guaranteed.</p>
                  <p style={{ marginBottom: '16px' }}>
                    Any Creator royalty or other benefit associated with secondary activity is only enforceable to the extent supported by the applicable contract, blockchain, platform, marketplace, or transaction structure.
                  </p>

                  <p style={{ marginBottom: '8px' }}><strong>15. FEES AND SUBSCRIPTION CHARGES</strong></p>
                  <p style={{ marginBottom: '10px' }}>
                    Creators are responsible for subscription fees, implementation fees, transaction charges, creation fees, commissions, or other charges disclosed for their applicable plan or service.
                  </p>
                  <p style={{ marginBottom: '10px' }}>
                    Promotional, pilot, internal, early-user, founder, or other discounts may reduce the amount charged without changing the underlying standard price of the applicable subscription.
                  </p>
                  <p style={{ marginBottom: '16px' }}>
                    Unless otherwise stated, receiving a promotional price does not create a permanent entitlement to that promotional rate.
                  </p>

                  <p style={{ marginBottom: '8px' }}><strong>16. REFUNDS AND NON-REFUNDABLE TRANSACTIONS</strong></p>
                  <p style={{ marginBottom: '10px' }}>
                    Subscription refunds, if any, are governed by the applicable Kickback Contract subscription and refund policy.
                  </p>
                  <p style={{ marginBottom: '10px' }}>
                    A Creator should not assume that every blockchain, marketplace, financial, transaction, deployment, processing, or third-party fee can be reversed after it has been incurred.
                  </p>
                  <p style={{ marginBottom: '16px' }}>
                    Where a particular transaction or commitment is non-refundable, that restriction should be disclosed before the Creator authorizes the applicable transaction.
                  </p>

                  <p style={{ marginBottom: '8px' }}><strong>17. PROHIBITED USES</strong></p>
                  <p style={{ marginBottom: '10px' }}>
                    Creators may not use Kickback Contract to facilitate unlawful activity, fraud, deceptive promotions, prohibited financial schemes, infringement, unauthorized sales, or any program prohibited by applicable platform rules.
                  </p>
                  <p style={{ marginBottom: '16px' }}>
                    Kickback Contract may restrict or reject programs involving elevated financial, regulatory, consumer-protection, fraud, or operational risk.
                  </p>

                  <p style={{ marginBottom: '8px' }}><strong>18. NO PROFESSIONAL ADVICE</strong></p>
                  <p style={{ marginBottom: '10px' }}>
                    Kickback Contract provides software, tools, workflows, and related platform services.
                  </p>
                  <p style={{ marginBottom: '10px' }}>
                    Unless separately and expressly provided by an appropriately qualified professional, information presented by the platform does not constitute individualized legal, tax, accounting, securities, investment, or financial advice.
                  </p>
                  <p style={{ marginBottom: '16px' }}>
                    Creators remain responsible for obtaining professional advice appropriate to their circumstances.
                  </p>

                  <p style={{ marginBottom: '8px' }}><strong>19. PLATFORM CHANGES</strong></p>
                  <p style={{ marginBottom: '10px' }}>Kickback Contract is an evolving platform.</p>
                  <p style={{ marginBottom: '10px' }}>
                    Features, workflows, subscription entitlements, integrations, provider relationships, eligibility requirements, and technical functionality may change as the platform develops.
                  </p>
                  <p style={{ marginBottom: '16px' }}>
                    Material changes affecting a Creator's active obligations or paid service may require additional notice, agreement, or acceptance where appropriate.
                  </p>

                  <p style={{ marginBottom: '8px' }}><strong>20. SUSPENSION OR TERMINATION</strong></p>
                  <p style={{ marginBottom: '10px' }}>
                    Kickback Contract may suspend or terminate access for material violations of these terms, fraudulent activity, unlawful conduct, security threats, payment failure, misuse of platform infrastructure, or conduct that presents significant risk to users or the platform.
                  </p>
                  <p style={{ marginBottom: '16px' }}>
                    Where reasonably possible, Creators may be provided an opportunity to resolve correctable issues before permanent termination.
                  </p>

                  <p style={{ marginBottom: '8px' }}><strong>21. DATA AND RECORDS</strong></p>
                  <p style={{ marginBottom: '10px' }}>
                    The Creator authorizes Kickback Contract to process information reasonably necessary to operate the Creator's account, Kickbacks, utilities, holders, redemptions, transactions, support requests, analytics, and platform services.
                  </p>
                  <p style={{ marginBottom: '16px' }}>
                    Additional data practices are governed by the applicable Kickback Contract privacy policy.
                  </p>

                  <p style={{ marginBottom: '8px' }}><strong>22. INTELLECTUAL PROPERTY</strong></p>
                  <p style={{ marginBottom: '10px' }}>
                    Creators retain their rights in original Creator-provided trademarks, artwork, product information, business content, and other intellectual property, subject to the permissions reasonably necessary for Kickback Contract to host, display, process, distribute, or technically operate the Creator's Kickbacks.
                  </p>
                  <p style={{ marginBottom: '16px' }}>
                    The Kickback Contract platform, software, branding, system architecture, proprietary workflows, and related intellectual property remain the property of their respective owners.
                  </p>

                  <p style={{ marginBottom: '8px' }}><strong>23. NO GUARANTEE OF BUSINESS RESULTS</strong></p>
                  <p style={{ marginBottom: '10px' }}>
                    Kickback Contract does not guarantee sales, customer retention, revenue, resale demand, engagement, profitability, appreciation, participation, or commercial success.
                  </p>
                  <p style={{ marginBottom: '16px' }}>
                    Results depend upon numerous factors outside the platform's control, including the Creator's offer, pricing, utilities, customers, fulfillment, promotion, market demand, and implementation.
                  </p>

                  <p style={{ marginBottom: '8px' }}><strong>24. ADDITIONAL AGREEMENTS FOR ADVANCED PROGRAMS</strong></p>
                  <p style={{ marginBottom: '10px' }}>
                    Advanced financial or economic-participation functionality may require a separate agreement before activation.
                  </p>
                  <p style={{ marginBottom: '10px' }}>This may include additional:</p>
                  <ul style={{ marginBottom: '10px', paddingLeft: '20px' }}>
                    <li>risk disclosures</li>
                    <li>participant eligibility standards</li>
                    <li>provider agreements</li>
                    <li>custody terms</li>
                    <li>distribution terms</li>
                    <li>compliance certifications</li>
                    <li>identity verification</li>
                    <li>jurisdictional restrictions</li>
                    <li>taxation provisions</li>
                    <li>transaction-specific acknowledgments</li>
                  </ul>
                  <p style={{ marginBottom: '16px' }}>
                    Acceptance of this Creator Platform Agreement alone does NOT activate those advanced programs.
                  </p>

                  <p style={{ marginBottom: '8px' }}><strong>25. CREATOR ACKNOWLEDGMENT</strong></p>
                  <p style={{ marginBottom: '10px' }}>By selecting the acceptance checkbox, the Creator confirms:</p>
                  <p style={{ marginBottom: '10px' }}>
                    I have read and understand the Kickback Contract Creator Platform Agreement & Program Terms.
                  </p>
                  <p style={{ marginBottom: '10px' }}>
                    I understand that some functionality described as future, planned, conditional, advanced, or provider-dependent is not currently available merely because it is described in this Agreement.
                  </p>
                  <p style={{ marginBottom: '10px' }}>
                    I understand that I am responsible for the products, services, utilities, benefits, representations, and fulfillment commitments I create through my business.
                  </p>
                  <p style={{ marginBottom: '10px' }}>
                    I understand that Kickback Contract does not guarantee investment returns, income, appreciation, resale value, or business results.
                  </p>
                  <p style={{ marginBottom: '10px' }}>
                    I understand that advanced economic-participation programs may require separate eligibility, providers, disclosures, agreements, and compliance requirements before becoming available.
                  </p>
                  <p>
                    I agree to use Kickback Contract according to these terms and the rules applicable to my account and Kickbacks.
                  </p>
                </div>
              </div>

              <div style={{ marginBottom: '24px' }}>
                <label style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', cursor: 'pointer' }}>
                  <input
                    data-testid="agreement-checkbox"
                    type="checkbox"
                    checked={formData.agreementAccepted}
                    onChange={(e) => updateField('agreementAccepted', e.target.checked)}
                    style={{ width: '20px', height: '20px', marginTop: '2px', cursor: 'pointer' }}
                  />
                  <span style={{ fontSize: '14px', color: theme.colors.text.primary, lineHeight: '1.6' }}>
                    I have read, understand, and agree to the <strong>Kickback Contract Creator Platform Agreement & Program Terms</strong>, including the current platform rules, Creator responsibilities, pilot conditions, and disclosures concerning future or conditional advanced economic-participation features.
                  </span>
                </label>
              </div>

              <div>
                <label style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer' }}>
                  <input
                    data-testid="email-copy-checkbox"
                    type="checkbox"
                    checked={formData.sendEmailCopy}
                    onChange={(e) => updateField('sendEmailCopy', e.target.checked)}
                    style={{ cursor: 'pointer' }}
                  />
                  <span style={{ fontSize: '14px', color: theme.colors.text.secondary }}>
                    Send a copy of this agreement to my email
                  </span>
                </label>
              </div>
            </div>
          )}

          {/* ============ STEP 6: Workspace Ready ============ */}
          {currentStep === 6 && (
            <div data-testid="onboarding-step-6">
              <h2 style={{ fontSize: '24px', fontWeight: 700, color: theme.colors.text.primary, marginBottom: '8px' }}>
                Your Kickback Workspace Is Ready
              </h2>
              <p style={{ fontSize: '14px', color: theme.colors.text.secondary, marginBottom: '24px' }}>
                Here's a snapshot of what you told us. You can update any of this later from your workspace.
              </p>

              <div style={{
                background: theme.colors.bg.secondary,
                padding: '20px',
                borderRadius: theme.radius.md,
                border: `1px solid ${theme.colors.borders}`,
                marginBottom: '24px',
              }} data-testid="onboarding-summary">
                <SummaryRow label="Business / Creator" value={formData.brandName} />
                <SummaryRow label="Business Type" value={formData.businessType} />
                <SummaryRow label="Primary Industry" value={formData.categories.join(', ')} />
                <SummaryRow label="Primarily Sells" value={formData.offeringTypes.join(', ')} />
                <SummaryRow label="Primary Goal" value={formData.primaryGoal} />
                <SummaryRow
                  label="Ownership / Value Interests"
                  value={[
                    ...formData.utilitiesAccess,
                    ...formData.ownership,
                    ...formData.participation,
                  ].join(', ')}
                />
                <SummaryRow
                  label="First Kickback Direction"
                  value={
                    formData.kickbackName
                      ? `${formData.kickbackName}${formData.kickbackType ? ' — ' + formData.kickbackType : ''}`
                      : '—'
                  }
                />
                {formData.advancedInterests.length > 0 && (
                  <SummaryRow
                    label="Advanced Interests (Future / Conditional)"
                    value={formData.advancedInterests.join(', ')}
                  />
                )}
              </div>

              <h3 style={groupHeadingStyle}>Recommended Next Steps</h3>
              <ol style={{ paddingLeft: '20px', marginBottom: '24px', lineHeight: '1.9', color: theme.colors.text.primary }}>
                <li><strong>Create Your First Kickback</strong> — turn what you sell into an ongoing customer relationship.</li>
                <li>Explore the Utility Vault — a catalog of ownership utilities you can attach to your Kickback.</li>
                <li>Review Recommended Templates — starting points tuned to your business type.</li>
                <li>Explore Kickback Architect Assistance — professional help when you're ready to expand.</li>
              </ol>

              <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                <button
                  data-testid="create-first-kickback-btn"
                  onClick={goCreateFirstKickback}
                  disabled={loading}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '8px',
                    padding: '14px 24px',
                    border: 'none',
                    borderRadius: theme.radius.sm,
                    background: theme.colors.primary,
                    color: '#FFFFFF',
                    fontSize: '15px', fontWeight: 600,
                    cursor: loading ? 'wait' : 'pointer',
                    opacity: loading ? 0.7 : 1,
                  }}
                >
                  {loading ? 'Saving…' : 'Create My First Kickback'} <ChevronRight size={18} />
                </button>
                <button
                  data-testid="go-to-dashboard-btn"
                  onClick={goDashboard}
                  disabled={loading}
                  style={{
                    padding: '14px 24px',
                    border: `1px solid ${theme.colors.borders}`,
                    borderRadius: theme.radius.sm,
                    background: '#FFFFFF',
                    color: theme.colors.text.primary,
                    fontSize: '15px', fontWeight: 600,
                    cursor: loading ? 'wait' : 'pointer',
                    opacity: loading ? 0.7 : 1,
                  }}
                >
                  Go to Dashboard
                </button>
              </div>
            </div>
          )}

          {/* Nav buttons (hidden on step 6 which has its own CTAs) */}
          {currentStep < TOTAL_STEPS && (
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              marginTop: '40px',
              paddingTop: '24px',
              borderTop: `1px solid ${theme.colors.borders}`,
            }}>
              <button
                data-testid="onboarding-back-btn"
                onClick={handleBack}
                disabled={currentStep === 1}
                style={{
                  display: 'flex', alignItems: 'center', gap: '8px',
                  padding: '12px 24px',
                  border: `1px solid ${theme.colors.borders}`,
                  borderRadius: theme.radius.sm,
                  background: '#FFFFFF',
                  color: theme.colors.text.primary,
                  fontSize: '14px', fontWeight: 600,
                  cursor: currentStep === 1 ? 'not-allowed' : 'pointer',
                  opacity: currentStep === 1 ? 0.5 : 1,
                }}
              >
                <ChevronLeft size={18} /> Back
              </button>

              <button
                data-testid="onboarding-continue-btn"
                onClick={handleNext}
                disabled={!canProceed() || loading}
                style={{
                  display: 'flex', alignItems: 'center', gap: '8px',
                  padding: '12px 32px',
                  border: 'none',
                  borderRadius: theme.radius.sm,
                  background: canProceed() && !loading ? theme.colors.primary : theme.colors.bg.tertiary,
                  color: '#FFFFFF',
                  fontSize: '14px', fontWeight: 600,
                  cursor: canProceed() && !loading ? 'pointer' : 'not-allowed',
                  opacity: loading ? 0.7 : 1,
                }}
              >
                Continue <ChevronRight size={18} />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const SummaryRow = ({ label, value }) => (
  <div style={{ display: 'flex', gap: '12px', padding: '6px 0', borderBottom: `1px solid ${theme.colors.borders}` }}>
    <div style={{ minWidth: '220px', fontSize: '13px', color: theme.colors.text.secondary, fontWeight: 500 }}>
      {label}
    </div>
    <div style={{ flex: 1, fontSize: '14px', color: theme.colors.text.primary, fontWeight: 500 }}>
      {value && value.length > 0 ? value : <span style={{ color: theme.colors.text.secondary, fontWeight: 400 }}>—</span>}
    </div>
  </div>
);

export default CreatorOnboardingRebuilt;
